import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { first } from 'lodash';
import { iif, of } from 'rxjs';
import { catchError, exhaustMap, filter, map, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators';

import { AppliancesService } from '@app/services/appliances.service';
import { BidsService } from '@app/services/bids.service';
import { CompaniesService } from '@app/services/companies.service';
import { FileUploadsService } from '@app/services/file-uploads.service';
import { TicketsService } from '@app/services/tickets.service';
import { TurnoversService } from '@app/services/turnovers.service';
import { WorkflowActionsService } from '@app/services/workflow-actions.service';
import { WorkflowStepCommentsService } from '@app/services/workflow-step-comments.service';
import { resetTurnoversFilters, setTurnoversFilters } from '@dashboards/store';
import { TurnoverConfigurationStepAssigneesService } from '@main-application/management/components/turnover-configuration/components/turnover-configuration-step-assignees/services/turnover-configuration-step-assignees.service';
import {
  addNewBid,
  addNewBidFail,
  addNewBidSuccess,
  addNewTurnoverTicket,
  addNewTurnoverTicketFailed,
  addNewTurnoverTicketSuccess,
  addWorkflowStepComment,
  addWorkflowStepCommentFailed,
  addWorkflowStepCommentSuccess,
  assignDefaultWorkflowStepAssignee,
  assignDefaultWorkflowStepAssigneeFailed,
  createNewTurnover,
  createNewTurnoverFailed,
  createNewTurnoverSuccess,
  deleteBidAttachment,
  deleteBidAttachmentFailed,
  deleteBidAttachmentSuccess,
  deleteFileFromRepository,
  deleteFileFromRepositoryFailed,
  deleteFileFromRepositorySuccess,
  deleteTurnover,
  deleteTurnoverAttachment,
  deleteTurnoverAttachmentSuccess,
  deleteTurnoverFailed,
  deleteTurnoverSuccess,
  deleteTurnoverTicket,
  deleteTurnoverTicketAttachment,
  deleteTurnoverTicketAttachmentFailed,
  deleteTurnoverTicketAttachmentSuccess,
  deleteTurnoverTicketFailed,
  deleteTurnoverTicketSuccess,
  deleteWorkflowStepComment,
  deleteWorkflowStepCommentFailed,
  deleteWorkflowStepCommentSuccess,
  emptyAction,
  getAllBidList,
  getAllBidListFail,
  getAllBidListSuccess,
  getAppliance,
  getApplianceFailed,
  getApplianceSuccess,
  getBid,
  getBidAttachmentList,
  getBidAttachmentListFailed,
  getBidAttachmentListSuccess,
  getBidCompanyList,
  getBidCompanyListFail,
  getBidCompanyListSuccess,
  getBidFail,
  getBidSuccess,
  getBidTurnoverList,
  getBidTurnoverListFail,
  getBidTurnoverListSuccess,
  getCompanyList,
  getCompanyListFail,
  getCompanyListSuccess,
  getDefaultTurnoversAssignees,
  getDefaultTurnoversAssigneesFailed,
  getDefaultTurnoversAssigneesSuccess,
  getSelectedTicket,
  getSelectedTicketFailed,
  getSelectedTicketSuccess,
  getTicket,
  getTicketAttachments,
  getTicketAttachmentsFailed,
  getTicketAttachmentsSuccess,
  getTicketFailed,
  getTicketSuccess,
  getTurnoverAttachments,
  getTurnoverAttachmentsFailed,
  getTurnoverAttachmentsSuccess,
  getTurnoverData,
  getTurnoverDataFailed,
  getTurnoverDataSuccess,
  getTurnoverTickets,
  getTurnoverTicketsFailed,
  getTurnoverTicketsSuccess,
  getWorkflowStepCommentList,
  getWorkflowStepCommentListFailed,
  getWorkflowStepCommentListSuccess,
  loadTurnoverTimers,
  rejectBid,
  rejectBidFail,
  rejectBidSuccess,
  setActiveSections,
  setAttachmentToTurnover,
  setAttachmentToTurnoverFailed,
  setAttachmentToTurnoverSuccess,
  setBidAttachment,
  setBidAttachmentFail,
  setBidAttachmentSuccess,
  setEmptyWorkflowStepAssignee,
  setInactiveFilteredSections,
  setStepAssignee,
  setStepAssigneeFailed,
  setStepAssigneeSuccess,
  setTicketAttachment,
  setTicketAttachmentFail,
  setTicketAttachmentSuccess,
  setTurnoverData,
  setTurnoverDataFailed,
  setTurnoverTimers,
  setTurnoverTimersFailed,
  setTurnoverTimersSuccess,
  setWorkflowStepAssignee,
  setWorkflowStepAssigneeFailed,
  setWorkflowStepAssigneeSuccess,
  setWorkflowStepSuccess,
  toggleAllSections,
  toggleInactiveFilteredSingleSection,
  toggleSingleSection,
  turnoverTimersLoaded,
  updateBid,
  updateBidComment,
  updateBidCommentFail,
  updateBidCommentSuccess,
  updateBidFail,
  updateBidSuccess,
  updateTicketPreview,
  updateTicketPreviewFailed,
  updateTicketPreviewSuccess,
  updateTurnoverAttachment,
  updateTurnoverAttachmentFailed,
  updateTurnoverAttachmentSuccess,
  updateTurnoverTicket,
  updateTurnoverTicketFailed,
  updateTurnoverTicketSuccess,
  updateWorkflowStepComment,
  updateWorkflowStepCommentFailed,
  updateWorkflowStepCommentSuccess,
  workflowAssignmentsByPropertyAndStepIdFailure,
  workflowAssignmentsByPropertyAndStepIdSucceded,
} from '@main-application/turnovers/actions/turnovers.actions';
import {
  selectActivePropertyIds,
  selectAllPropertyIds,
  selectInactiveFilteredPropertyIds,
  selectTurnoverData,
} from '@main-application/turnovers/selectors/turnovers.selectors';
import { TicketType } from '@shared/enums/ticket-type';
import { WorkflowStepEnumType } from '@shared/enums/workflow-step.enum';
import { UpdateWorkflowStepAssigneeModelFunction } from '@shared/functions/update-workflow-step-responsible-party-model.function';
import { RestGenericTypedAttachment } from '@shared/interfaces/attachment.interface';
import { RestTypedBidAttachment } from '@shared/interfaces/bid.interface';
import { RestWorkflowStepAssignmentModel } from '@shared/interfaces/rest-workflow-step-assignment-model.interface';
import {
  RestTicketModel,
  RestTurnoverDataModel,
  RestTypedTicketAttachment,
} from '@shared/interfaces/turnover.interface';
import { selectUserData } from '@shared/selectors/user.selectors';
import { SnackbarErrorMessage } from '@ui-components/components/customized-snackbar/snackbar-message.enum';
import { SnackbarService } from '@ui-components/components/customized-snackbar/snackbar.service';

import { loadPunchById } from '../actions/punch.actions';

@Injectable()
export class TurnoversEffects {
  constructor(
    private actions$: Actions,
    private store: Store<{}>,
    private snackbarService: SnackbarService,
    private turnoversService: TurnoversService,
    private workflowActionsService: WorkflowActionsService,
    private ticketsService: TicketsService,
    private companiesService: CompaniesService,
    private bidsService: BidsService,
    private appliancesService: AppliancesService,
    private workflowStepCommentsService: WorkflowStepCommentsService,
    private responsiblePartiesService: TurnoverConfigurationStepAssigneesService,
    private fileUploadsService: FileUploadsService
  ) {}

  createTurnover$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createNewTurnover),
      switchMap(action =>
        this.turnoversService.create(action.postTurnoverModel).pipe(
          mergeMap(turnoverData => [
            createNewTurnoverSuccess({ turnoverData }),
            assignDefaultWorkflowStepAssignee({
              turnoverData,
              workflowStep: turnoverData.activeWorkflowStepType,
              propertyId: action.postTurnoverModel?.propertyId,
            }),
          ]),
          catchError(err => {
            this.snackbarService.error(SnackbarErrorMessage.CreatingTurnover);
            return of(createNewTurnoverFailed());
          })
        )
      )
    )
  );

  assignDefaultWorkflowStepAssignee$ = createEffect(() =>
    this.actions$.pipe(
      ofType(assignDefaultWorkflowStepAssignee),
      withLatestFrom(this.store.select(selectUserData)),
      filter(([action, userData]) => {
        return !first(
          (action.turnoverData.workflow || []).filter(item => item.workflowStepEnumType === action.workflowStep)
        )?.responsiblePartyId;
      }),
      switchMap(([action, userData]) =>
        this.responsiblePartiesService.getWorkflowAssignmentsByStepId(action.propertyId, action.workflowStep).pipe(
          filter(
            (restWorkflowStepAssignmentModel: RestWorkflowStepAssignmentModel) =>
              !!restWorkflowStepAssignmentModel || (action.workflowStep === WorkflowStepEnumType.MoveOut && !!userData)
          ),
          map(workflowStepAssignmentModel => {
            return {
              ...action.turnoverData,
              workflow: UpdateWorkflowStepAssigneeModelFunction(
                action.turnoverData.workflow,
                action.workflowStep,
                workflowStepAssignmentModel,
                userData
              ),
            };
          }),
          switchMap((turnoverData: RestTurnoverDataModel) => {
            return this.turnoversService.update(turnoverData).pipe(
              mergeMap(turnoverData => [getTurnoverData({ turnoverId: turnoverData.id })]),
              catchError(() => {
                this.snackbarService.error(SnackbarErrorMessage.AssigningAssignee);
                return of(assignDefaultWorkflowStepAssigneeFailed());
              })
            );
          })
        )
      )
    )
  );

  getTurnoverData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getTurnoverData),
      switchMap(action =>
        this.turnoversService.getTurnover(action.turnoverId).pipe(
          map(turnoverData => getTurnoverDataSuccess({ turnoverData })),
          catchError(ex => {
            this.snackbarService.error(SnackbarErrorMessage.RetrievingTurnoverData);
            return of(getTurnoverDataFailed());
          })
        )
      )
    )
  );

  setTurnoverData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setTurnoverData),
      switchMap(action => {
        return this.turnoversService.update(action.turnoverData).pipe(
          map(turnoverData => getTurnoverDataSuccess({ turnoverData })),
          catchError(() => {
            this.snackbarService.error(SnackbarErrorMessage.UpdatingTurnoverData);
            return of(setTurnoverDataFailed());
          })
        );
      })
    )
  );

  setStepAssignee$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setStepAssignee),
      switchMap(action => {
        return this.workflowActionsService
          .moveToNextStep(
            action.workflowAction.turnoverId,
            action.workflowAction.nextWorkflowStep,
            action.workflowAction.assigneeId
          )
          .pipe(
            map(() => setStepAssigneeSuccess()),
            catchError(() => {
              this.snackbarService.error(SnackbarErrorMessage.ErrorUpdatingTurnoverStepAssignee);
              return of(setStepAssigneeFailed());
            })
          );
      })
    )
  );

  getDefaultTurnoversAssignees$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getDefaultTurnoversAssignees),
      switchMap(action => {
        return this.responsiblePartiesService.getResponsiblePartiesByPropertyId(action.propertyId).pipe(
          map(list => getDefaultTurnoversAssigneesSuccess({ defaultTurnoversAssignees: list || [] })),
          catchError(() => {
            this.snackbarService.error(SnackbarErrorMessage.ErrorRetrievingDefaultTurnoverAssignees);
            return of(getDefaultTurnoversAssigneesFailed());
          })
        );
      })
    )
  );

  updateDefaultAssigneeOnWorkflowStepChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setWorkflowStepSuccess),
      switchMap(action =>
        this.turnoversService.getTurnover(action.turnoverId).pipe(
          map(turnoverData =>
            assignDefaultWorkflowStepAssignee({
              turnoverData,
              workflowStep: action.nextWorkflowStep,
              propertyId: turnoverData.property?.id,
            })
          )
        )
      )
    )
  );

  setAttachmentToTurnover$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setAttachmentToTurnover),
      mergeMap(action =>
        this.turnoversService.setAttachment(action.attachmentToEntity).pipe(
          mergeMap(addedAttachment => [
            setAttachmentToTurnoverSuccess({ addedAttachment }),
            !action?.preventReloadList
              ? getTurnoverAttachments({ turnoverId: first(action.attachmentToEntity.turnoverIds) })
              : emptyAction(),
          ]),
          catchError(() => {
            this.snackbarService.error(SnackbarErrorMessage.AssigningAttachmentToEntity);
            return of(setAttachmentToTurnoverFailed());
          })
        )
      )
    )
  );

  updateTurnoverAttachment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateTurnoverAttachment),
      mergeMap(action =>
        this.turnoversService.updateAttachment(action.attachmentToEntity).pipe(
          mergeMap(addedAttachment => [
            updateTurnoverAttachmentSuccess(),
            getTurnoverAttachments({ turnoverId: first(action.attachmentToEntity.turnoverIds) }),
          ]),
          catchError(() => {
            this.snackbarService.error(SnackbarErrorMessage.UpdatingAttachment);
            return of(updateTurnoverAttachmentFailed());
          })
        )
      )
    )
  );

  getTurnoverTickets$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getTurnoverTickets),
      switchMap(action =>
        this.ticketsService.getTicketsByTurnover(action.turnoverId).pipe(
          map(list => getTurnoverTicketsSuccess({ turnoverTicketList: list || [] })),
          catchError(() => {
            this.snackbarService.error(SnackbarErrorMessage.RetrievingTurnoverTickets);
            return of(getTurnoverTicketsFailed());
          })
        )
      )
    )
  );

  addNewTurnoverTicket$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addNewTurnoverTicket),
      mergeMap(action => {
        return this.ticketsService.create(action.turnoverTicket).pipe(
          mergeMap(newTurnoverTicket => [addNewTurnoverTicketSuccess({ newTurnoverTicket })]),
          tap(() => {
            this.snackbarService.success(SnackbarErrorMessage.TicketCreated);
          }),
          catchError(err => {
            this.snackbarService.error(SnackbarErrorMessage.CreatingTicket);
            return of(addNewTurnoverTicketFailed());
          })
        );
      })
    )
  );

  updateTurnoverTicket$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateTurnoverTicket),
      mergeMap(action =>
        this.ticketsService.update(action.turnoverTicket).pipe(
          mergeMap(editedTicket => [
            updateTurnoverTicketSuccess({ editedTicket }),
            getTurnoverTickets({ turnoverId: editedTicket.turnoverId }),
          ]),
          tap(() => {
            this.snackbarService.info(SnackbarErrorMessage.TicketUpdated);
          }),
          catchError(() => {
            this.snackbarService.error(SnackbarErrorMessage.UpdatingTurnoverTicket);
            return of(updateTurnoverTicketFailed());
          })
        )
      )
    )
  );
  updateTurnoverTicketPreview$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateTicketPreview),
      mergeMap(action =>
        this.ticketsService.update(action.turnoverTicket).pipe(
          mergeMap(editedTicket => [updateTicketPreviewSuccess({ editedTicket })]),
          tap(() => {
            this.snackbarService.info(SnackbarErrorMessage.TicketUpdated);
          }),
          catchError(() => {
            this.snackbarService.error(SnackbarErrorMessage.UpdatingTicket);
            return of(updateTicketPreviewFailed());
          })
        )
      )
    )
  );

  getTurnoverTicket$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getSelectedTicket),
      switchMap(action =>
        this.ticketsService.getTicket(action.ticketId).pipe(
          map(ticket => getSelectedTicketSuccess({ ticket })),
          catchError(() => {
            this.snackbarService.error(SnackbarErrorMessage.RetrievingTurnoverTicket);
            return of(getSelectedTicketFailed());
          })
        )
      )
    )
  );

  deleteTurnoverTicket$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteTurnoverTicket),
      withLatestFrom(this.store.select(selectTurnoverData)),
      mergeMap(([action, turnoverData]) =>
        this.ticketsService.delete(action.ticketId).pipe(
          mergeMap(() => [deleteTurnoverTicketSuccess(), getTurnoverTickets({ turnoverId: turnoverData.id })]),
          catchError(() => {
            this.snackbarService.error(SnackbarErrorMessage.DeletingTicket);
            return of(deleteTurnoverTicketFailed());
          })
        )
      )
    )
  );

  setTicketAttachment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setTicketAttachment),
      mergeMap(action =>
        this.ticketsService.setAttachment(action.attachmentToEntity).pipe(
          mergeMap(addedTicketAttachment => [
            setTicketAttachmentSuccess({ addedTicketAttachment }),
            action.ticketType === TicketType.Ticket
              ? getSelectedTicket({ ticketId: action.attachmentToEntity.ticketId })
              : loadPunchById({ punchId: action.attachmentToEntity.ticketId }),
          ]),
          catchError(() => {
            this.snackbarService.error(SnackbarErrorMessage.AssigningAttachmentToTicket);
            return of(setTicketAttachmentFail());
          })
        )
      )
    )
  );

  getTicket$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getTicket),
      switchMap(action =>
        this.ticketsService.getTicket(action.ticketId).pipe(
          map((restTicketModel: RestTicketModel) => getTicketSuccess({ restTicketModel })),
          catchError(() => {
            this.snackbarService.error(SnackbarErrorMessage.RetrievingTicket);
            return of(getTicketFailed());
          })
        )
      )
    )
  );

  getTicketAttachments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getTicketAttachments),
      switchMap(action =>
        this.ticketsService.getTicketAttachment(action.ticketId).pipe(
          map((attachments: RestTypedTicketAttachment[]) =>
            getTicketAttachmentsSuccess({ ticketAttachments: attachments || [] })
          ),
          catchError(() => {
            this.snackbarService.error(SnackbarErrorMessage.RetrievingTicketAttachment);
            return of(getTicketAttachmentsFailed());
          })
        )
      )
    )
  );

  getCompanyList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getCompanyList),
      exhaustMap(action =>
        this.companiesService.getCompanyList().pipe(
          map(companyList => getCompanyListSuccess({ companyList })),
          catchError(() => {
            this.snackbarService.error(SnackbarErrorMessage.RetrievingCompaniesList);
            return of(getCompanyListFail());
          })
        )
      )
    )
  );

  getBidTurnoverList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getBidTurnoverList),
      switchMap(action =>
        this.bidsService.getTurnoverBids(action.turnoverId).pipe(
          map(bidList => getBidTurnoverListSuccess({ bidList })),
          catchError(ex => {
            this.snackbarService.error(SnackbarErrorMessage.RetrievingBidList);
            return of(getBidTurnoverListFail());
          })
        )
      )
    )
  );

  getBidCompanyList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getBidCompanyList),
      switchMap(action =>
        this.bidsService.getCompanyBids(action.companyId).pipe(
          map(bidList => getBidCompanyListSuccess({ bidList })),
          catchError(() => {
            this.snackbarService.error(SnackbarErrorMessage.RetrievingVendorsList);
            return of(getBidCompanyListFail());
          })
        )
      )
    )
  );

  getAllBidList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getAllBidList),
      switchMap(action =>
        this.bidsService.getAllBids().pipe(
          map(list => getAllBidListSuccess({ bidList: list || [] })),
          catchError(ex => {
            this.snackbarService.error(SnackbarErrorMessage.RetrievingBidList);
            return of(getAllBidListFail());
          })
        )
      )
    )
  );

  getBid$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getBid),
      switchMap(action =>
        this.bidsService.getBid(action.bidId).pipe(
          map(bid => getBidSuccess({ bid })),
          catchError(() => {
            this.snackbarService.error(SnackbarErrorMessage.RetrievingBid);
            return of(getBidFail());
          })
        )
      )
    )
  );

  addNewBid$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addNewBid),
      mergeMap(action =>
        this.bidsService.create(action.bidList).pipe(
          map(newBidList => addNewBidSuccess({ newBidList })),
          catchError(() => {
            this.snackbarService.error(SnackbarErrorMessage.AddingBid);
            return of(addNewBidFail());
          })
        )
      )
    )
  );

  rejectBid$ = createEffect(() =>
    this.actions$.pipe(
      ofType(rejectBid),
      mergeMap(action =>
        this.bidsService.reject(action.id).pipe(
          mergeMap(updatedBid => [
            rejectBidSuccess({ updatedBid }),
            getBidTurnoverList({ turnoverId: first(updatedBid.turnoverIds) }),
          ]),
          catchError(() => {
            this.snackbarService.error(SnackbarErrorMessage.UpdatingBid);
            return of(rejectBidFail());
          })
        )
      )
    )
  );

  updateBid$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateBid),
      mergeMap(action =>
        this.bidsService.update(action.bid).pipe(
          mergeMap(updatedBid => [updateBidSuccess({ updatedBid })]),
          catchError(() => {
            this.snackbarService.error(SnackbarErrorMessage.UpdatingBid);
            return of(updateBidFail());
          })
        )
      )
    )
  );

  updateBidComment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateBidComment),
      mergeMap(action =>
        this.bidsService.update(action.bid).pipe(
          map(updatedBid => updateBidCommentSuccess({ updatedBid })),
          catchError(() => {
            this.snackbarService.error(SnackbarErrorMessage.UpdatingBid);
            return of(updateBidCommentFail());
          })
        )
      )
    )
  );

  setBidAttachment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setBidAttachment),
      mergeMap(action =>
        this.bidsService.setAttachment(action.attachmentToEntity).pipe(
          mergeMap((addedBidAttachment: RestTypedBidAttachment) => [
            setBidAttachmentSuccess({ addedBidAttachment }),
            getBidAttachmentList({ bidId: action.attachmentToEntity.bidId }),
          ]),
          catchError(() => {
            this.snackbarService.error(SnackbarErrorMessage.AssigningAttachmentToBid);
            return of(setBidAttachmentFail());
          })
        )
      )
    )
  );

  getAppliance$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getAppliance),
      switchMap(action =>
        this.appliancesService.getAppliance(action.applianceId).pipe(
          map(appliance => getApplianceSuccess({ appliance })),
          catchError(() => {
            this.snackbarService.error(SnackbarErrorMessage.RetrievingApplianceData);
            return of(getApplianceFailed());
          })
        )
      )
    )
  );

  getWorkflowStepCommentList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getWorkflowStepCommentList),
      switchMap(action =>
        this.workflowStepCommentsService.getWorkflowStepComments(action.turnoverId).pipe(
          map(workflowStepCommentList => getWorkflowStepCommentListSuccess({ workflowStepCommentList })),
          catchError(() => {
            this.snackbarService.error(SnackbarErrorMessage.ReceivingTurnoverComments);
            return of(getWorkflowStepCommentListFailed());
          })
        )
      )
    )
  );

  addWorkflowStepComment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addWorkflowStepComment),
      mergeMap(action =>
        this.workflowStepCommentsService.create(action.commentItem).pipe(
          mergeMap(commentItem => [
            addWorkflowStepCommentSuccess({ commentItem }),
            getWorkflowStepCommentList({ turnoverId: commentItem.turnoverDataId }),
          ]),
          catchError(() => {
            this.snackbarService.error(SnackbarErrorMessage.AddingComment);
            return of(addWorkflowStepCommentFailed());
          })
        )
      )
    )
  );

  updateWorkflowStepComment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateWorkflowStepComment),
      mergeMap(action =>
        this.workflowStepCommentsService.update(action.commentItem).pipe(
          mergeMap(commentItem => [
            updateWorkflowStepCommentSuccess({ commentItem }),
            getWorkflowStepCommentList({ turnoverId: commentItem.turnoverDataId }),
          ]),
          catchError(() => {
            this.snackbarService.error(SnackbarErrorMessage.UpdatingComment);
            return of(updateWorkflowStepCommentFailed());
          })
        )
      )
    )
  );

  deleteWorkflowStepComment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteWorkflowStepComment),
      mergeMap(action =>
        this.workflowStepCommentsService.delete(action.commentItem.id).pipe(
          mergeMap(workflowStepCommentList => [
            deleteWorkflowStepCommentSuccess(),
            getWorkflowStepCommentList({ turnoverId: action.commentItem.turnoverDataId }),
          ]),
          catchError(() => {
            this.snackbarService.error(SnackbarErrorMessage.Deletingcomment);
            return of(deleteWorkflowStepCommentFailed());
          })
        )
      )
    )
  );

  // loadWorkflowAssignmentsByPropertyAndStepId$ = createEffect(() =>
  //   this.actions$.pipe(
  //     ofType(loadWorkflowAssignmentsByPropertyAndStepId),
  //     switchMap(action =>
  //       this.responsiblePartiesService.getWorkflowAssignmentsByStepId(action.propertyId, action.stepId).pipe(
  //         map(workflowAssignment =>
  //           workflowAssignmentsByPropertyAndStepIdSucceded({
  //             workflowStepAssigneeId: workflowAssignment?.responsiblePartyId || null,
  //           })
  //         ),
  //         catchError(() => {
  //           this.snackbarService.error(SnackbarErrorMessage.LoadingworkflowAssignment);
  //           return of(workflowAssignmentsByPropertyAndStepIdFailure());
  //         })
  //       )
  //     )
  //   )
  // );

  deleteTurnover$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteTurnover),
      mergeMap(action =>
        this.turnoversService.deleteTurnover(action.turnoverId).pipe(
          map(workflowAssignment => deleteTurnoverSuccess()),
          catchError(() => {
            this.snackbarService.error(SnackbarErrorMessage.DeletingTurnover);
            return of(deleteTurnoverFailed());
          })
        )
      )
    )
  );

  deleteTurnoverAttachment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteTurnoverAttachment),
      withLatestFrom(this.store.select(selectTurnoverData)),
      mergeMap(([action, turnoverData]) =>
        this.turnoversService.deleteTurnoverAttachment(turnoverData.id, action.attachment.id).pipe(
          switchMap(() =>
            iif(
              () => !!action.attachment?.fileUploadId,
              of(action.attachment?.fileUploadId).pipe(
                map((fileId: number) => deleteFileFromRepository({ fileId: action.attachment?.fileUploadId }))
              ),
              of(turnoverData.id).pipe(map((turnoverId: number) => deleteFileFromRepositorySuccess()))
            )
          ),
          mergeMap(() => [deleteTurnoverAttachmentSuccess(), getTurnoverAttachments({ turnoverId: turnoverData.id })]),
          catchError(() => {
            this.snackbarService.error(SnackbarErrorMessage.DeletingAttachment);
            return of(deleteFileFromRepositoryFailed());
          })
        )
      )
    )
  );

  deleteTurnoverTicketAttachment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteTurnoverTicketAttachment),
      mergeMap(action =>
        this.ticketsService.deleteTurnoverAttachment(action.ticketId, action.attachment.id).pipe(
          switchMap(() =>
            iif(
              () => !!action.attachment?.fileUploadId,
              of(action.attachment?.fileUploadId).pipe(map((fileId: number) => deleteFileFromRepository({ fileId }))),
              of(null)
            )
          ),
          mergeMap(() => [
            deleteTurnoverTicketAttachmentSuccess(),
            action.ticketType === TicketType.Ticket
              ? getTicket({ ticketId: action.ticketId })
              : loadPunchById({ punchId: action.ticketId }),
          ]),
          catchError(() => {
            this.snackbarService.error(SnackbarErrorMessage.DeletingAttachment);
            return of(deleteTurnoverTicketAttachmentFailed());
          })
        )
      )
    )
  );

  deleteFileFromRepository$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteFileFromRepository),
      mergeMap(action =>
        this.fileUploadsService.removeFile(action.fileId).pipe(
          map(() => deleteFileFromRepositorySuccess()),
          catchError(() => {
            this.snackbarService.error(SnackbarErrorMessage.DeletingFileFromRepository);
            return of(deleteFileFromRepositoryFailed());
          })
        )
      )
    )
  );

  getTurnoverAttachments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getTurnoverAttachments),
      mergeMap(action =>
        this.turnoversService.getTurnoversAttachments(action.turnoverId).pipe(
          map((attachments: RestGenericTypedAttachment[]) =>
            getTurnoverAttachmentsSuccess({ allAttachments: attachments || [] })
          ),
          catchError(() => {
            this.snackbarService.error(SnackbarErrorMessage.RetrievingTurnoverAttachmentList);
            return of(getTurnoverAttachmentsFailed());
          })
        )
      )
    )
  );

  getBidAttachmentList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getBidAttachmentList),
      mergeMap(action =>
        this.bidsService.getBidAttachments(action.bidId).pipe(
          map((attachments: RestGenericTypedAttachment[]) =>
            getBidAttachmentListSuccess({ allAttachments: attachments || [] })
          ),
          catchError(() => {
            this.snackbarService.error(SnackbarErrorMessage.RetrievingBidAttachmentList);
            return of(getBidAttachmentListFailed());
          })
        )
      )
    )
  );

  deleteBidAttachment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteBidAttachment),
      mergeMap(action =>
        this.bidsService.deleteBidAttachment(action.bidId, action.attachment.id).pipe(
          switchMap(() =>
            iif(
              () => !!action.attachment?.fileUploadId,
              of(action.attachment?.fileUploadId).pipe(map((fileId: number) => deleteFileFromRepository({ fileId }))),
              of(null)
            )
          ),
          mergeMap(() => [deleteBidAttachmentSuccess(), getBidAttachmentList({ bidId: action.bidId })]),
          catchError(() => {
            this.snackbarService.error(SnackbarErrorMessage.DeletingAttachment);
            return of(deleteBidAttachmentFailed());
          })
        )
      )
    )
  );

  toggleAllSections$ = createEffect(() =>
    this.actions$.pipe(
      ofType(toggleAllSections),
      withLatestFrom(this.store.select(selectAllPropertyIds)),
      mergeMap(([action, allPropertyIds]) =>
        this.turnoversService
          .toggleAllSections(allPropertyIds, action.active)
          .pipe(map(activePropertyIds => setActiveSections({ activePropertyIds })))
      )
    )
  );

  toggleSingleSection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(toggleSingleSection),
      withLatestFrom(this.store.select(selectActivePropertyIds)),
      mergeMap(([action, activePropertyIds]) =>
        this.turnoversService
          .toggleSingleSection(activePropertyIds, action.propertyId, action.active)
          .pipe(map(activePropertyIds => setActiveSections({ activePropertyIds })))
      )
    )
  );

  toggleInactiveFilteredSingleSection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(toggleInactiveFilteredSingleSection),
      withLatestFrom(this.store.select(selectInactiveFilteredPropertyIds)),
      mergeMap(([action, selectInactiveFilteredPropertyIds]) =>
        this.turnoversService
          .toggleSingleSection(selectInactiveFilteredPropertyIds, action.propertyId, action.active)
          .pipe(map(inactiveFilteredPropertyIds => setInactiveFilteredSections({ inactiveFilteredPropertyIds })))
      )
    )
  );

  resetInactiveFilteredSections$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setTurnoversFilters, resetTurnoversFilters),
      map(() => setInactiveFilteredSections({ inactiveFilteredPropertyIds: [] }))
    )
  );

  loadTurnoverTimers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadTurnoverTimers),
      mergeMap(action =>
        this.turnoversService.getTurnoversTimers(action.turnoverId).pipe(
          map(timers => turnoverTimersLoaded({ turnoverId: action.turnoverId, timers })),
          catchError(() => {
            return of(setTurnoverTimersFailed());
          })
        )
      )
    )
  );

  setTurnoverTimers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setTurnoverTimers),
      mergeMap(action =>
        this.turnoversService.updateTurnoversTimers(action.turnoverId, action.timers).pipe(
          map(() => setTurnoverTimersSuccess()),
          catchError(() => {
            this.snackbarService.error('Timer update failed');
            return of(setTurnoverTimersFailed());
          })
        )
      )
    )
  );

  setWorkflowStepAssignee$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setWorkflowStepAssignee),
      mergeMap(action =>
        this.workflowActionsService.setWorkflowStepAssignee(action.turnoverId, action.step, action.assigneeId).pipe(
          mergeMap(() => [
            setWorkflowStepAssigneeSuccess(),
            getWorkflowStepCommentList({ turnoverId: action.turnoverId }),
          ]),
          catchError(() => {
            return of(setWorkflowStepAssigneeFailed());
          })
        )
      )
    )
  );

  setEmptyWorkflowStepAssignee$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setEmptyWorkflowStepAssignee),
      mergeMap(action =>
        this.workflowActionsService
          .setWorkflowStepAssignee(action.turnoverId, action.step, action.assigneeId, action.rememberIt)
          .pipe(
            mergeMap(() => [getTurnoverData({ ...action })]),
            catchError(() => {
              return of(setWorkflowStepAssigneeFailed());
            })
          )
      )
    )
  );
}
